/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';

import CampaignModel from 'cio/models/campaign';
import NewsletterModel from 'cio/models/newsletter';
import SegmentModel from 'cio/models/segment';
import TagModel from 'cio/models/tag';

// Ideally you'd use window.getComputedStyle but it adds more work for what is
// essentially a constant.
const TAG_MARGIN = 8;

interface TagListSignature {
  Args: {
    model: CampaignModel | NewsletterModel | SegmentModel;
    onTagClicked: (tag: TagModel) => unknown;
  };
  Element: HTMLDivElement;
}

export default class TagList extends Component<TagListSignature> {
  @tracked initialized = false;
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked tagCount = this.args.model.sortedTags.length;
  @tracked showAll = false;

  containerElement: HTMLElement | null = null;
  buttonWidth = 0;
  tagWidths: number[] | null = null;

  get remainingCount() {
    return this.args.model.sortedTags.length - this.tagCount;
  }

  get visibleTags() {
    let { sortedTags } = this.args.model;
    let count = this.showAll ? sortedTags.length : this.tagCount;
    return sortedTags.slice(0, count);
  }

  @action
  handleResize() {
    // Reset tagCount to the full tag amount so the recalculations
    // for how many fit in total are accurate
    this.tagCount = this.args.model.sortedTags.length;

    scheduleOnce('afterRender', this, this.recalculateVisibleTags);
  }

  @action
  recalculateVisibleTags() {
    if (this.containerElement == null) {
      return;
    }

    let containerWidth = Math.floor(this.containerElement.offsetWidth);
    let availableWidth = containerWidth - this.buttonWidth;
    if (this.tagWidths == null) {
      this.tagWidths = [...this.containerElement.querySelectorAll('.js-tag')].map(
        (element: HTMLElement) => element.offsetWidth + TAG_MARGIN,
      );
    }

    let tagsWidth = 0;
    let tagIndex = 0;

    while (tagsWidth < containerWidth || tagIndex < this.tagWidths.length) {
      let tagWidth = this.tagWidths[tagIndex];

      if (tagsWidth + tagWidth >= availableWidth) {
        break;
      }

      tagsWidth += tagWidth;
      tagIndex++;
    }

    this.tagCount = tagIndex;
    this.initialized = true;
  }

  @action
  handleInsert(containerElement: HTMLElement) {
    this.containerElement = containerElement;

    window.addEventListener('resize', this.handleResize);
    let button = this.containerElement.querySelector('.js-tags-button') as HTMLElement;
    this.buttonWidth = button?.offsetWidth ?? 0;
    this.handleResize();
  }

  @action onTagClicked(tag: TagModel) {
    this.args.onTagClicked?.(tag);
  }

  willDestroy() {
    super.willDestroy();
    window.removeEventListener('resize', this.handleResize);
  }
}
